<template>
  <VeeForm v-slot="{ errors, handleSubmit, meta }" as="">
    <div
      class="flex flex-col justify-between rounded-md mt-1 w-full bg-brand-primary bg-opacity-5 border border-critical-secondary border-opacity-20 darkTheme:border-opacity-10 h-full"
    >
      <form autocomplete="off" class="overflow-auto py-3 px-4">
        <VeeField
          v-slot="{ field }"
          v-model="formModel.quantity"
          name="quantity"
          :rules="{
            required: true,
            min: [symbol.minimumOrderQuantity, t('modules.order.order_count')],
            max: [symbol.maximumOrderQuantity, t('modules.order.order_count')],
            quantityTick: symbol.lotSize,
          }"
        >
          <NInput
            v-mask="{
              alias: 'positiveNumber',
            }"
            v-focus
            :model-value="formModel.quantity"
            input-class="text-center font-bold"
            v-bind="field"
            :error="errors.quantity"
            :required-sign="false"
            :show-error="true"
            type="tel"
            dir="ltr"
            class="mb-2"
            required
            size="small"
            :step="symbol.lotSize"
            :min="0"
            @focusin="$event.target.select()"
            @update:model-value="changeQuantity($event)"
          >
            <template #prefix>
              <NText
                variant="caption1"
                weight="medium"
                class="text-neutral-primary"
              >
                {{ t('misc.quantity') }}
              </NText>
            </template>

            <template #postfix>
              <div class="flex justify-center items-center">
                <div class="flex flex-col cursor-pointer">
                  <NumberFormatter
                    #="{ number }"
                    :value="symbol.maximumOrderQuantity"
                  >
                    <NText
                      dir="ltr"
                      tag="p"
                      variant="caption2"
                      @click="changeQuantity(symbol.maximumOrderQuantity ?? 0)"
                    >
                      {{ number }}
                    </NText>
                  </NumberFormatter>
                  <NumberFormatter
                    #="{ number }"
                    :value="symbol.minimumOrderQuantity"
                  >
                    <NText
                      dir="ltr"
                      tag="p"
                      variant="caption2"
                      @click="changeQuantity(symbol.minimumOrderQuantity ?? 0)"
                    >
                      {{ number }}
                    </NText>
                  </NumberFormatter>
                </div>
              </div>
            </template>
          </NInput>
        </VeeField>

        <div class="mb-4 flex items-center justify-between px-3">
          <NText variant="caption1">
            {{ t('option.openPositionsCount') }}
          </NText>

          <div
            :class="
              openPositionsCount === 0
                ? 'bg-neutral-secondary text-neutral-secondary'
                : isBuyPosition
                  ? 'bg-brand-secondary text-brand-quaternary'
                  : 'bg-critical-secondary text-critical-primary'
            "
            class="px-2 py-0.5 rounded-md cursor-pointer"
            @click="changeQuantity(openPositionsCount)"
          >
            <NumberFormatter #="{ number }" :value="openPositionsCount">
              <NText variant="caption1">
                {{ number }}
              </NText>
            </NumberFormatter>
          </div>
        </div>

        <div class="mb-4">
          <VeeField
            v-slot="{ field }"
            v-model="formModel.price"
            name="price"
            :rules="{
              required: true,
              between: [
                symbol.lowerPriceThreshold,
                symbol.upperPriceThreshold,
                t('modules.order.order_price'),
              ],
              priceTick: symbol.fixedPriceTick,
            }"
          >
            <NInput
              v-mask="{
                alias: 'positiveNumber',
              }"
              :model-value="formModel.price"
              input-class="text-center font-bold"
              v-bind="field"
              :error="errors.price"
              :required-sign="false"
              size="small"
              :show-error="true"
              type="tel"
              dir="ltr"
              required
              :min="0"
              class="!mb-1"
              :step="symbol.fixedPriceTick"
              :disabled="isLinkToPrice"
              @focusin="$event.target.select()"
              @update:model-value="changePrice($event)"
            >
              <template #prefix>
                <NText
                  variant="caption1"
                  weight="medium"
                  class="text-neutral-primary"
                >
                  {{ t('common.price') }}
                </NText>
              </template>

              <template #postfix>
                <div class="flex justify-center items-center">
                  <div class="flex flex-col cursor-pointer">
                    <NumberFormatter
                      #="{ number }"
                      :value="symbol.upperPriceThreshold"
                    >
                      <NText
                        v-gid="{
                          module: 'order',
                          screen: 'home',
                          section: 'optionBuyOrderDialog',
                          component: 'maxPriceButton',
                        }"
                        dir="ltr"
                        tag="p"
                        variant="caption2"
                        @click="changePrice(symbol.upperPriceThreshold)"
                      >
                        {{ number }}
                      </NText>
                    </NumberFormatter>
                    <NumberFormatter
                      #="{ number }"
                      :value="symbol.lowerPriceThreshold"
                    >
                      <NText
                        v-gid="{
                          module: 'order',
                          screen: 'home',
                          section: 'optionBuyOrderDialog',
                          component: 'minPriceButton',
                        }"
                        dir="ltr"
                        tag="p"
                        variant="caption2"
                        @click="changePrice(symbol.lowerPriceThreshold)"
                      >
                        {{ number }}
                      </NText>
                    </NumberFormatter>
                  </div>

                  <NegarLinkIcon
                    class="ms-2 cursor-pointer"
                    :class="{ 'text-brand-primary': isLinkToPrice }"
                    @click="changeLink"
                  />
                </div>
              </template>
            </NInput>
          </VeeField>

          <div v-if="!isValidPrice" class="flex">
            <NegarErrorLightIcon class="text-warning-secondary" size="16" />

            <NText variant="overline" class="text-neutral-primary ms-1">
              {{ t('option.fairPrice.validationForBuy') }}
            </NText>
          </div>
        </div>
        <VeeField
          v-slot="{ field }"
          v-model="formModel.totalValue"
          name="totalValue"
          :rules="{
            required: true,
          }"
        >
          <NInput
            v-mask="{
              alias: 'positiveNumber',
            }"
            :model-value="formModel.totalValue"
            v-bind="field"
            input-class="text-center font-bold"
            :error="errors.totalValue"
            :required-sign="false"
            :show-error="true"
            type="tel"
            dir="ltr"
            class="mb-2"
            size="small"
            :min="0"
            required
            @focusin="$event.target.select()"
            @update:model-value="changeTotalValue($event)"
          >
            <template #prefix>
              <NText
                variant="caption1"
                weight="medium"
                class="text-neutral-primary"
              >
                {{ t('misc.value') }}
              </NText>
            </template>

            <template #postfix>
              <NText variant="caption1" class="text-neutral-secondary">
                {{ t('misc.rial') }}
              </NText>
            </template>
          </NInput>
        </VeeField>

        <div class="flex items-center justify-between px-3">
          <NText weight="medium" variant="overline">
            {{ t('option.buyingPower') }}
          </NText>

          <div>
            <NumberFormatter #="{ number }" :value="cash?.optionBuyingPower">
              <NText weight="medium" variant="overline">
                {{ number }}
              </NText>

              <NText weight="medium" variant="overline" class="mr-1">
                {{ t('misc.rial') }}
              </NText>
            </NumberFormatter>
          </div>
        </div>

        <Slider
          :model-value="formModel.totalValue ?? 0"
          :max-value="cash.optionBuyingPower"
          :show-range-label="false"
          @change="changeTotalValue"
        />

        <div v-if="isShowValidityTypeOptions" class="mt-6 text-center">
          <template v-for="(value, key) in VALIDITY_TYPES" :key="key">
            <button
              class="px-3 py-2 rounded-md ml-1 mb-2 cursor-pointer"
              type="button"
              :class="[
                formModel.validityType === value
                  ? 'bg-brand-secondary text-brand-primary'
                  : 'text-neutral-secondary-hover',
              ]"
              @click="formModel.validityType = value"
            >
              <NText
                variant="caption2"
                :weight="[
                  formModel.validityType === value ? 'bold' : 'regular',
                ]"
              >
                {{ VALIDITY_TYPE_LABEL[value] }}
              </NText>
            </button>
          </template>
        </div>

        <Datepicker
          v-if="formModel.validityType === VALIDITY_TYPES.GoodTillDate"
          v-model="formModel.validityDate"
          is-local-date-time
          class="mt-3 mb-2 w-auto"
        />

        <div class="flex justify-center mt-1">
          <div
            class="flex items-center justify-center p-2 active:bg-neutral-primary-press w-fit rounded-md cursor-pointer"
            @click="isShowValidityTypeOptions = !isShowValidityTypeOptions"
          >
            <NText>
              {{ validityTypeLabel }}
            </NText>
            <NegarArrowDownIcon
              :size="20"
              :class="[
                'transform transition',
                {
                  'rotate-180': isShowValidityTypeOptions,
                },
              ]"
            />
          </div>
        </div>
      </form>

      <div
        class="bg-neutral-primary flex gap-2 justify-center items-center w-full p-3 rounded-b-md border border-brand-primary border-opacity-10"
      >
        <NButton
          v-gid="{
            module: 'order',
            screen: 'home',
            section: 'optionBuyOrderDialog',
            component: 'submitOrderButton',
            disabled: !meta.valid || isOrderStatusPending || loading,
          }"
          variant="fill"
          class="font-bold flex-[2]"
          type="submit"
          size="small"
          :loading="loading"
          :disabled="!meta.valid || isOrderStatusPending || loading"
          @click="handleSubmit($event, onSubmit)"
        >
          {{
            isEditOrder
              ? t('modules.order.editOrder')
              : t('modules.order.sendBuyOrder')
          }}
        </NButton>

        <NButton
          v-if="!isEditOrder"
          v-gid="{
            module: 'order',
            screen: 'home',
            section: `optionBuyOrderDialog`,
            component: 'sendAndCloseButton',
            disabled: loading || !meta.valid || isOrderStatusPending,
          }"
          variant="outline"
          class="font-bold text-c1"
          color="green"
          size="small"
          :disabled="loading || !meta.valid || isOrderStatusPending"
          @click="
            (event) => {
              shouldCloseDialogAfterOrderExecution = true;
              handleSubmit(event, onSubmit);
            }
          "
        >
          {{ t('modules.order.sendAndClose') }}
        </NButton>
      </div>
    </div>
  </VeeForm>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  NegarLinkIcon,
  NegarArrowDownIcon,
  NegarErrorLightIcon,
} from '@negar/icons';
import { useDialog } from '@negar/core';
import { useLiveCashQuery } from '@core/cash';
import { useSymbolDeductions } from '@core/symbol';
import {
  useLiveGetOptionContract,
  validatePrice,
  calcSymbolBuyValueOption as calcOptionValue,
  calcOptionSymbolQuantityBuy,
  useOptionPosition,
} from '@core/option';
import {
  ORDER_SIDES,
  ORDER_TYPES,
  VALIDITY_TYPE_LABEL,
  VALIDITY_TYPES,
} from '@core/order';
import { Instrument, OrderItem } from '@libs/api-reference';
import { omitBy } from '@libs/utils';

import { useCreateOrder } from '@/modules/order';

import NumberFormatter from '@/components/Number.vue';
import Datepicker from '@/components/Datepicker/index.vue';
import Slider from '@/components/Slider.vue';

import vMask from '@/directives/mask';
import vFocus from '@/directives/focus';

import eventBus from '@/composables/event/eventBus';

type FormModel = {
  quantity?: number | null;
  price?: number | null;
  totalValue?: number | null;
  validityDate: Date;
  validityType: number;
  remainingQuantity?: number | null;
};

type OptionBuyOrderFormProps = {
  symbol: Instrument;
  model: {
    quantity?: null | number;
    price?: null | number;
  };
  isLinkToPrice?: boolean;
  dialogId?: string;
  isEditOrder?: boolean;
  isOrderStatusPending?: boolean;
  selectedOrder?: OrderItem;
};

const props = withDefaults(defineProps<OptionBuyOrderFormProps>(), {
  dialogId: undefined,
  selectedOrder: undefined,
});

const BASE_ORDER_FORM_MODEL: FormModel = {
  quantity: props.model.quantity,
  price: props.model.price,
  totalValue: null,
  validityDate: new Date(),
  validityType: VALIDITY_TYPES.Day,
  remainingQuantity: props.model.quantity,
};

const isLinkToPrice = ref(props.isLinkToPrice);
const shouldCloseDialogAfterOrderExecution = ref(false);
const isShowValidityTypeOptions = ref(false);
const isinRef = ref(props.symbol.isin as string);

const { quantity: openPositionsCount, positionSide } = useOptionPosition({
  isin: props.symbol.isin,
});

const formModel = reactive<FormModel>({ ...BASE_ORDER_FORM_MODEL });

const { t, d } = useI18n();
const { selectedDialogId } = useDialog();

const { cashData: cash } = useLiveCashQuery();

const { findWageConfigBySymbol } = useSymbolDeductions();

const { isLoadingCreateOrder, createOrder, updateOrder, isLoadingUpdateOrder } =
  useCreateOrder({
    isin: props.symbol.isin as string,
    orderType: ORDER_TYPES.buy,
    selectedOrder: props.selectedOrder,
    instrumentTypeCode: props.symbol.instrumentTypeCode as string,
  });

const loading = computed(
  () => isLoadingCreateOrder.value || isLoadingUpdateOrder.value
);

const { optionContract } = useLiveGetOptionContract({
  isin: isinRef,
});

const isBuyPosition = computed(() => positionSide.value === ORDER_SIDES.buy);

const isValidPrice = computed(() => {
  if (!formModel.price || !optionContract.value) return true;

  const fairPrice = optionContract.value.blackScholes?.fairPrice;
  const previousDayClosingPrice = optionContract.value.previousDayClosingPrice;

  return validatePrice(
    formModel.price,
    fairPrice,
    previousDayClosingPrice,
    'buy'
  );
});

const wage = findWageConfigBySymbol(props.symbol);

const realValue = computed(() =>
  calcOptionValue(
    {
      quantity: formModel.quantity || 0,
      contractSize: optionContract.value?.contractSize || 0,
      lastTradePrice: formModel.price || 0,
    },
    wage
  )
);

const validityTypeLabel = computed(() =>
  formModel.validityType === VALIDITY_TYPES.GoodTillDate
    ? `${t('orders.valid_until')} ${d(formModel.validityDate, 'shortDate')}`
    : VALIDITY_TYPE_LABEL[formModel.validityType]
);

const quantity = computed(() => {
  let roundUp = false;

  if (formModel.totalValue) {
    roundUp = formModel.totalValue >= cash.value.buyingPower;
  }

  return calcOptionSymbolQuantityBuy(
    formModel,
    wage,
    roundUp,
    optionContract.value?.contractSize || 0
  );
});

const changeTotalValue = (value: number) => {
  formModel.totalValue = value;

  if (formModel.price) {
    formModel.quantity = quantity.value;
  }
};

const changePrice = (value: number | null) => {
  formModel.price = value;

  if (formModel.quantity) {
    formModel.totalValue = realValue.value;
  } else if (formModel.totalValue) {
    formModel.quantity = quantity.value;
  }
};

const changeQuantity = (value: number) => {
  formModel.quantity = value;

  if (formModel.price) {
    formModel.totalValue = realValue.value;
  }
};

const changeLink = () => {
  isLinkToPrice.value = !isLinkToPrice.value;

  changePrice(headlinePrices.value as number);
};

const onSubmit = (_, { resetForm }) => {
  const resetFormModel = () => {
    Object.assign(formModel, BASE_ORDER_FORM_MODEL);
    resetForm({
      values: { ...BASE_ORDER_FORM_MODEL },
    });
  };

  if (props.selectedOrder) {
    updateOrder({
      formModel,
      resetFormModel,
      shouldCloseDialog: shouldCloseDialogAfterOrderExecution.value,
      isOption: true,
    });

    return;
  }

  createOrder({
    formModel,
    resetFormModel,
    shouldCloseDialog: shouldCloseDialogAfterOrderExecution.value,
    isOption: true,
  });
};

const validRangePrice = (price) =>
  props.symbol.upperPriceThreshold && props.symbol.lowerPriceThreshold
    ? !(
        price > props.symbol?.upperPriceThreshold ||
        price < props.symbol?.lowerPriceThreshold
      )
    : true;

// Calculate maximum buy price and minimum sell price in bidAsk table for link to headline
const headlinePrices = computed(() => {
  if (props.symbol?.bidAsk?.length) {
    const validRangeForBidPrice: number[] = [];

    props.symbol?.bidAsk?.map((o) => {
      if (validRangePrice(o.bidPrice)) {
        validRangeForBidPrice.push(o.bidPrice);
      }
    });

    const minBidPrice = Math.min(...validRangeForBidPrice);

    return validRangeForBidPrice.length
      ? minBidPrice
      : props.symbol.upperPriceThreshold;
  }

  return props.symbol.lastTradePrice;
});

eventBus.on('executeOrder', (shouldClose) => {
  if (selectedDialogId.value === props.dialogId && !loading.value) {
    shouldCloseDialogAfterOrderExecution.value = shouldClose;

    onSubmit(formModel, {
      resetForm: () => {
        Object.assign(formModel, BASE_ORDER_FORM_MODEL);
      },
    });
  }
});

watch(
  headlinePrices,
  () => {
    if (isLinkToPrice.value) {
      changePrice(headlinePrices.value as number);
    }
  },
  {
    immediate: true,
  }
);

watch(
  () => props.model,
  (newModel) => {
    const sanitizedModel = omitBy(
      newModel,
      (v) => v === null || v === undefined
    );

    if (Object.keys(sanitizedModel).length) {
      Object.assign(formModel, sanitizedModel);
    }

    if (formModel.price) {
      changePrice(formModel.price);
    }

    if (formModel.quantity) {
      changeQuantity(formModel.quantity);
    }
  },
  {
    deep: true,
    immediate: true,
  }
);
</script>
